export const mergeCustomFields = (custom_fields, availableCustomFields) => {
    custom_fields.forEach((custom_field) => {
        const availableCustomField = availableCustomFields.find((acf) => acf._id === custom_field.custom_field_id);
        if (availableCustomField) {
            availableCustomField.value = custom_field.value;
        }
    });

    return availableCustomFields.map((acf) => ({ ...acf, custom_field_id: acf._id }));
};

const getValue = ({ data_type, value }) => {
    if (data_type === 'date') return value ? new Date(value) : undefined;
    if (data_type === 'number') return value ? parseFloat(value) : undefined;
    return value ?? null;
};

export const formatCustomFields = (customFields) => {
    if (!customFields) return [];
    return customFields.map((field) => ({ custom_field_id: field.custom_field_id, value: getValue(field) }));
};

export const dataTypesMap = {
    string: 'Text',
    number: 'Number',
    date: 'Date',
    boolean: 'Checkbox',
    select: 'Select',
    multi_line: 'Multi-Line',
};

export const producerCategoriesMap = {
    account_details: 'Account Details',
    information: 'Information',
    physical_location: 'Physical Location',
    pay_information: 'Pay Information',
    tank_management: 'Tank Management',
    pickup_details: 'Pickup Details',
    hauling_details: 'Hauling Details',
};

/**
 * The `information` category feeds each role's "… Information" profile card, so the Field
 * Location dropdown labels it per role instead of the generic 'Information'. Roles without
 * an entry here (admin, driver, producer_business — none mount this form today) fall back
 * to the map label.
 */
export const informationLabelByRole = {
    producer: 'Producer Information',
    processor: 'Processor Information',
    transport: 'Hauling Information',
};

export const getCategoryLabel = (category, role) => {
    if (category === 'information') return informationLabelByRole[role] ?? producerCategoriesMap.information;
    return producerCategoriesMap[category];
};

/**
 * Parses the min/max bounds of a custom field into what the API expects.
 *
 * Both are optional server-side but validated as number-or-date, so a bound that is blank or
 * unparseable has to be left out of the payload entirely — sending `''` or `NaN` fails the
 * whole request with `"min" must be one of [number, date]`. Returns only the bounds worth
 * sending, so the caller can spread it.
 */
export const parseCustomFieldBounds = ({ data_type, min, max }) => {
    const isDate = data_type === 'date';

    return Object.entries({ min, max }).reduce((bounds, [bound, value]) => {
        if (value == null || value === '') return bounds;

        const parsed = isDate ? new Date(value) : parseFloat(value);
        const isValid = isDate ? !Number.isNaN(parsed.getTime()) : !Number.isNaN(parsed);

        return isValid ? { ...bounds, [bound]: parsed } : bounds;
    }, {});
};

export const UNCATEGORIZED_LABEL = 'Uncategorized';

/**
 * Buckets custom fields by their category so the Custom Fields section can render one
 * subheader per category. Groups follow producerCategoriesMap order, empty ones are dropped.
 *
 * Each item keeps the field's index in the flat array it came from, because that index is
 * what CustomField turns into its `custom_fields.${index}.*` form paths — grouping must not
 * renumber them. Fields whose category is missing or unrecognized (e.g. created before the
 * category enum grew, or after `pickups_and_haulings` was retired) go in a trailing group
 * rather than disappearing. The `information` group label is role-aware via getCategoryLabel.
 */
export const groupCustomFieldsByCategory = (customFields, role) => {
    if (!customFields?.length) return [];

    const buckets = new Map(Object.keys(producerCategoriesMap).map((category) => [category, []]));
    const uncategorized = [];

    customFields.forEach((field, index) => {
        const bucket = buckets.get(field?.category);
        (bucket ?? uncategorized).push({ field, index });
    });

    const groups = [];
    buckets.forEach((items, category) => {
        if (items.length) groups.push({ category, label: getCategoryLabel(category, role), items });
    });
    if (uncategorized.length) {
        groups.push({ category: 'uncategorized', label: UNCATEGORIZED_LABEL, items: uncategorized });
    }

    return groups;
};

/** Custom field definitions for one category, in the order they were returned. */
export const selectCustomFieldsByCategory = (customFields, category) => (customFields ?? []).filter((field) => field?.category === category);

/** Renders a stored custom field value for display where there is no input to format it. */
export const formatCustomFieldDisplayValue = ({ data_type, options }, value) => {
    if (value == null || value === '') return '—';
    if (data_type === 'boolean') return value ? 'Yes' : 'No';
    if (data_type === 'date') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
    }
    if (data_type === 'select') return options?.includes(value) ? value : '—';
    return String(value);
};
